import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ASK_A_QUESTION_ANALYTICS_PLACEHOLDER,
  MESSAGE_ORIGIN_PREPOPULATED
} from '../components/MagicApronAssistant/constants';
import { createActionId, getActiveSessionId } from '../utils/session-utils';
import { getPageType } from '../utils/window-utils';
import { waitForRefValue } from '../utils/common';
import { logger } from '../utils/logger';

/**
 * Analytics needs the index as a number, so anything else is dropped rather than reported wrong.
 *
 * @param { any } position
 * @return { number | undefined }
 */
const toPosition = (position) => {
  if (position === undefined || Number.isFinite(position)) {
    return position;
  }

  logger.error(`[MAA] useAskMagicApron: position must be a number, got ${typeof position}`);
  return undefined;
};

/**
 * Reports a click the assistant will never see, with the ids it would have used. Session read at
 * report time, not at mount: the assistant may have started its own since.
 *
 * @param { Object } bus - LIFE_CYCLE_EVENT_BUS.
 * @param { Object } click - What was clicked, as analytics names it.
 * @param { Object } [state] - Anything else worth reporting about this particular click.
 */
const reportQuestionAsk = (bus, { userQuestion, entryLocationForAnalytics, pageType }, state = {}) => {
  const sessionID = getActiveSessionId();

  bus.trigger('magic-apron-assistant.question-ask', {
    userQuestion,
    entryLocationForAnalytics,
    pageType,
    ...state,
    sessionID,
    actionID: createActionId(sessionID)
  });
};

/**
 * The public way for a component outside the assistant to open Magic Apron.
 *
 * Magic Apron builds the payload; a consumer supplies only what Magic Apron cannot know — the entry
 * point's location, and the question the shopper clicked. Nothing a consumer passes can drift from
 * the assistant's own session, page type or attribution.
 *
 * Events go to `window`, not `window.parent` as `AssistantProvider` uses: consumers render on the
 * host page, where reaching for `parent` from a cross-origin frame throws.
 *
 * @param { Object } [options]
 * @param { boolean } [options.enabled] - When false the hook opens no channel and both actions no-op.
 * @param { string } [options.entryLocationForAnalytics] - Where the entry point lives, for analytics.
 * @return {{
 *   askQuestion: function(string, { position?: number }=): Promise<void>,
 *   openAssistant: function(): void
 * }}
 */
export const useAskMagicApron = ({ enabled = true, entryLocationForAnalytics = '' } = {}) => {
  const channelRef = useRef(null);
  // Shaped for `waitForRefValue`, which resolves through `resolver` once the assistant is ready.
  const assistantReadyRef = useRef({ isReady: false, resolver: null });
  // One shared wait for every ask made before the assistant is ready. A second click must not start
  // its own: `waitForRefValue` rejects while a resolver is pending, dropping that question outright.
  const pendingAskRef = useRef({ promise: null, payload: null });
  // In a ref so both actions keep one identity, coerced so a non-string never reaches analytics.
  const entryLocationRef = useRef('');
  entryLocationRef.current = typeof entryLocationForAnalytics === 'string'
    ? entryLocationForAnalytics
    : '';

  /**
   * Reports a queued question that will never be handed over, and clears it so nothing reports or
   * delivers it twice. `handoverFailed` keeps analytics from reading it as a question answered.
   * Declared above the effect that calls it from teardown.
   */
  const flushPendingAsk = useCallback((bus) => {
    const { payload } = pendingAskRef.current;

    if (!payload) {
      return;
    }

    pendingAskRef.current.payload = null;

    reportQuestionAsk(bus, {
      userQuestion: payload.question,
      entryLocationForAnalytics: payload.entryLocationForAnalytics,
      pageType: payload.pageType
    }, { messageOrigin: payload.messageOrigin, handoverFailed: true });
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return undefined;
    }

    // In a local so teardown closes the channel this effect opened, whatever the ref holds.
    const channel = new BroadcastChannel('magic-apron-assistant');
    channelRef.current = channel;

    // Guarded apart from the channel: a missing bus must not stop the drawer from opening.
    const bus = window.LIFE_CYCLE_EVENT_BUS;

    if (!bus) {
      return () => {
        channel.close();
        channelRef.current = null;
      };
    }

    const isReadyListener = ({ output: { isReady } = {} }) => {
      assistantReadyRef.current.isReady = isReady;

      if (isReady && assistantReadyRef.current.resolver) {
        assistantReadyRef.current.resolver();
      }
    };

    bus.lifeCycle.on('magic-apron-assistant.isReady', isReadyListener);

    return () => {
      channel.close();
      channelRef.current = null;
      bus.lifeCycle.off('magic-apron-assistant.isReady', isReadyListener);
      // A question queued behind a boot that never finished would otherwise die when its wait times
      // out, by which point the page may be gone. Report it while there is still a bus to report on.
      flushPendingAsk(bus);
    };
  }, [enabled, flushPendingAsk]);

  /** Opens the drawer, reporting whether there was a channel to open it with. */
  const openDrawer = useCallback(() => {
    if (!channelRef.current) {
      return false;
    }

    channelRef.current.postMessage({ type: 'MAA_OPEN_DRAWER' });
    return true;
  }, []);

  /**
   * Opens the assistant and hands it a question to answer. Resolves once the question is handed
   * over, waiting for the assistant to finish booting when it has not yet.
   */
  const askQuestion = useCallback((question, { position } = {}) => {
    // A blank question would open an empty chat and report nothing. Refuse the call, and say why.
    if (typeof question !== 'string' || !question.trim()) {
      logger.error(
        '[MAA] useAskMagicApron: askQuestion needs a question to hand over — '
        + 'call openAssistant() to open the drawer with nothing prepopulated.'
      );
      return Promise.resolve();
    }

    if (!openDrawer()) {
      return Promise.resolve();
    }

    // The drawer is open by here, so a missing bus costs the handover, not the interaction.
    const bus = window.LIFE_CYCLE_EVENT_BUS;

    if (!bus) {
      return Promise.resolve();
    }

    // No ids: the assistant mints them for a handed-over question in `askUserQuestion`, and
    // reports the ask from there.
    const payload = {
      question,
      position: toPosition(position),
      entryLocationForAnalytics: entryLocationRef.current,
      pageType: getPageType().toLowerCase(),
      messageOrigin: MESSAGE_ORIGIN_PREPOPULATED
    };

    if (assistantReadyRef.current.isReady) {
      bus.lifeCycle.trigger('magic-apron-assistant.user-ask-question', payload);
      return Promise.resolve();
    }

    // A second click while the assistant boots replaces the queued question rather than queueing
    // beside it: the shopper wants the one they clicked last, not both posted into one chat.
    pendingAskRef.current.payload = payload;

    if (!pendingAskRef.current.promise) {
      pendingAskRef.current.promise = waitForRefValue(assistantReadyRef, 'isReady')
        .then(() => {
          // Absent once teardown has reported the queued question as failed — delivering it now
          // would hand over a question already counted as never handed over.
          if (pendingAskRef.current.payload) {
            bus.lifeCycle.trigger(
              'magic-apron-assistant.user-ask-question',
              pendingAskRef.current.payload
            );
          }
        })
        .catch((err) => {
          logger.error(err);
          flushPendingAsk(bus);
        })
        .finally(() => { pendingAskRef.current = { promise: null, payload: null }; });
    }

    return pendingAskRef.current.promise;
  }, [openDrawer, flushPendingAsk]);

  /** Opens the assistant empty, for the shopper to type their own question. */
  const openAssistant = useCallback(() => {
    if (!openDrawer()) {
      return;
    }

    const bus = window.LIFE_CYCLE_EVENT_BUS;

    if (!bus) {
      return;
    }

    // Nothing is handed over, so the assistant never sees this click either.
    reportQuestionAsk(bus, {
      userQuestion: ASK_A_QUESTION_ANALYTICS_PLACEHOLDER,
      entryLocationForAnalytics: entryLocationRef.current,
      pageType: getPageType().toLowerCase()
    });
  }, [openDrawer]);

  // One identity, so the returned object is safe in a consumer's dependency array.
  return useMemo(() => ({ askQuestion, openAssistant }), [askQuestion, openAssistant]);
};
